import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef, MRT_ColumnFiltersState, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { ArchivePOSTransactions, fetchPOSTransactions, FetchPOSTransactionsResponse } from "../userPOSTransactionsApi"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { Order } from "../../../../../merchant/controlKitchenTypes"
import { Receipt, ReceiptDialog } from "../../../../../receipt/pages/ReceiptDialog"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { TableToolbarBanner } from "../../../../../merchant/merchant/production/components/TableToolbarBanner"
import { PaymentMethodCell } from "../../../../../merchant/merchant/production/components/PaymentMethodCell"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import {
    DateType,
    SelectDateTypeFilter,
} from "../../../../../../shared/components/filters/selectDateTypeFilter/SelectDateTypeFilter"
import {
    TableNumberOfItemsBox,
    TableSummaryWrap,
    TableTotalAmountBox,
} from "../../../../../../shared/components/table/TableSummaryBox"
import FloatingOrderActionBar from "../../../../../merchant/merchant/production/components/FloatingOrderActionBar"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectUserId } from "../../../../../login/loginSelectors"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"

const selectOrders = (data: FetchPOSTransactionsResponse) => data?.orders || []

export function UserDailyPurchases() {
    const { l10n } = useAppLocalization()
    const userId = useAppSelector(selectUserId)

    const [selectedRows, setSelectedRows] = useState<Order[]>()
    const tableInstanceRef = useRef<MRT_TableInstance<Order>>(null)
    const [dateType, setDateType] = useState<DateType>(DateType.ORDER_DATE)
    const [receipt, setReceipt] = useState<Receipt>()
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [showNotPrintedOrders, setShowNotPrintedOrders] = useState(false)

    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })

    const orderList = useApiQuery(fetchPOSTransactions, {
        queryName: ArchivePOSTransactions.ARCHIVE_POS_TRANSACTIONS,
        dependencies: {
            userId: userId ?? 0,
            startDate: dateState.startDate?.toISODate(),
            endDate: dateState.endDate?.toISODate(),
            dateType,
        },
        select: selectOrders,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(orderList)

    const columns = useMemo<MRT_ColumnDef<Order>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("user-archive-transactions-table-id"),
                accessorKey: "id",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("user-archive-transactions-table-transaction-time"),
                    accessorKey: "created",
                },
                (row) => row.created,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("user-archive-transactions-table-delivery-time"),
                    accessorKey: "updated",
                },
                (row) => row.updated,
            ),
            columnFactory.typeColumn({
                header: l10n.getString("production-order-payment-status"),
                accessorKey: "paymentDetails.method",
                Cell: ({ row }) => <PaymentMethodCell paymentDetails={row.original.paymentDetails} />,
            }),
            columnFactory.moneyColumn(
                { header: l10n.getString("office-purchase-amount"), id: "amount" },
                (row) => row.amount,
                true,
            ),
        ],
        [columnFactory],
    )

    function showReceipt(row: Order) {
        setReceipt({
            orderUid: row.uid,
            orderId: row.id,
            orderType: row.orderType,
        })
    }

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    const hasNonePrinted = () => {
        if (!orderList.data) {
            return
        }
        return (
            orderList.data.filter((order) => {
                return order.printDetails?.isPrinted === false
            }).length > 0
        )
    }

    const showAlertBanner = useMemo(() => hasNonePrinted(), [orderList.data])

    const handleClearSelected = useCallback(() => {
        setSelectedRows(undefined)
        setRowSelection({})
    }, [])

    return (
        <>
            <Grid container direction="column" wrap="nowrap">
                {showAlertBanner && (
                    <TableToolbarBanner
                        showNotPrintedOrders={showNotPrintedOrders}
                        setShowNotPrintedOrders={setShowNotPrintedOrders}
                    />
                )}

                <ApiQueryTable
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onColumnFiltersChange={setColumnFilters}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="office-purchase-receipt">
                            <MenuItem
                                onClick={() => {
                                    showReceipt(row.original)
                                    closeMenu()
                                }}
                                key={`receipt-${row.original.id}`}
                            >
                                Receipt
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="transactions-table-list-refund">
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                }}
                                key={`refund-${row.original.id}`}
                            >
                                Refund
                            </MenuItem>
                        </LocalizedStrict>,
                        <LocalizedStrict id="transactions-table-list-message-customer">
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                }}
                                key={`message-customer-${row.original.id}`}
                            >
                                Message customer
                            </MenuItem>
                        </LocalizedStrict>,
                    ]}
                    renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <SelectDateTypeFilter dateType={dateType} setDateType={setDateType} />
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                        </StandardTableFilter>
                    )}
                    renderBottomToolbarCustomActions={({ table }) => (
                        <TableSummaryWrap padding="sm">
                            <LocalizedStrict id="table-summary-box-number-of-orders" attrs={{ title: true }}>
                                <TableNumberOfItemsBox
                                    data={table.getFilteredRowModel().rows}
                                    title="Number of orders"
                                />
                            </LocalizedStrict>

                            <TableTotalAmountBox
                                data={table.getFilteredRowModel().rows}
                                accessor={(row) => row.original.amount}
                            />
                        </TableSummaryWrap>
                    )}
                    state={{
                        columnFilters,
                        rowSelection,
                    }}
                />
            </Grid>

            {selectedRows && (
                <FloatingOrderActionBar
                    selectedRows={selectedRows}
                    startDate={dateState.startDate ?? DateTime.now()}
                    endDate={dateState.endDate ?? DateTime.now()}
                    handleClearSelected={handleClearSelected}
                    hidePrintActions={true}
                />
            )}

            {receipt && (
                <ReceiptDialog
                    receipt={receipt}
                    orders={orderList.data}
                    open={receipt !== undefined}
                    onClose={() => setReceipt(undefined)}
                />
            )}
        </>
    )
}
